/**
 * CUSTOMER-R4.5 / R4.7-A — structured customer cancel reason codes for analytics.
 * Display copy is separate from the persisted reason_code.
 * R4.7-A: change_items / change_delivery_details removed from selectable chips
 * (use edit / return-to-cart instead). Codes remain accepted for historical payloads.
 */
export const CUSTOMER_CANCEL_REASON_CODES = [
  "changed_mind",
  "items_mistake",
  "change_items",
  "duplicate_order",
  "change_delivery_details",
  "delivery_issue",
  "payment_issue",
  "better_offer",
  "other",
] as const;

/** Selectable cancel chips — edit-substitute reasons excluded. */
export const CUSTOMER_CANCEL_REASON_SELECTABLE = [
  "changed_mind",
  "items_mistake",
  "delivery_issue",
  "payment_issue",
  "better_offer",
  "duplicate_order",
  "other",
] as const satisfies readonly (typeof CUSTOMER_CANCEL_REASON_CODES)[number][];

export type CustomerCancelReasonCode = (typeof CUSTOMER_CANCEL_REASON_CODES)[number];

export const CUSTOMER_CANCEL_REASON_UK: Record<CustomerCancelReasonCode, string> = {
  changed_mind: "Передумав/ла",
  items_mistake: "Помилився/лась у товарах",
  change_items: "Хочу змінити склад замовлення",
  duplicate_order: "Створив/ла дубль",
  change_delivery_details: "Потрібно змінити отримувача / адресу",
  delivery_issue: "Не підходить доставка",
  payment_issue: "Проблема з оплатою",
  better_offer: "Знайшов/ла вигіднішу пропозицію",
  other: "Інше",
};

export const CUSTOMER_CANCEL_REASON_EN: Record<CustomerCancelReasonCode, string> = {
  changed_mind: "Changed my mind",
  items_mistake: "Wrong items",
  change_items: "Want to change the order items",
  duplicate_order: "Duplicate order",
  change_delivery_details: "Need to change recipient / address",
  delivery_issue: "Delivery does not work for me",
  payment_issue: "Payment problem",
  better_offer: "Found a better offer",
  other: "Other",
};

export type CancelRescueKind = "cart" | "delivery" | "payment_help" | null;

export function cancelRescueKind(code: CustomerCancelReasonCode | null): CancelRescueKind {
  if (code === "items_mistake" || code === "change_items") return "cart";
  if (code === "change_delivery_details" || code === "delivery_issue") return "delivery";
  if (code === "payment_issue") return "payment_help";
  return null;
}

export function isCustomerCancelReasonCode(raw: string | null | undefined): raw is CustomerCancelReasonCode {
  return (CUSTOMER_CANCEL_REASON_CODES as readonly string[]).includes(String(raw ?? "").trim());
}

export function normalizeCustomerCancelReasonCode(
  raw: string | null | undefined,
): CustomerCancelReasonCode | null {
  const v = String(raw ?? "").trim();
  if (isCustomerCancelReasonCode(v)) return v;
  return null;
}
